import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { LibraryFormat, RecordStatus } from '../model/types';

interface GeneralTabProps {
  format: LibraryFormat;
  isSaving: boolean;
  onSave: (patch: {
    name: string;
    surface: string | null;
    aliases: string[];
    w: number;
    h: number;
    status: RecordStatus;
    notes: string | null;
  }) => void;
}

const positiveInteger = (value: string) => /^\d+$/.test(value) && Number(value) > 0;

export function GeneralTab({ format, isSaving, onSave }: GeneralTabProps) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState(format.name);
  const [surface, setSurface] = useState(format.surface ?? '');
  const [aliases, setAliases] = useState(format.aliases.join(', '));
  const [w, setW] = useState(String(format.w));
  const [h, setH] = useState(String(format.h));
  const [status, setStatus] = useState<RecordStatus>(format.status);
  const [notes, setNotes] = useState(format.notes ?? '');

  // A save or another person's edit hands the form a new revision of the same format.
  useEffect(() => {
    setName(format.name);
    setSurface(format.surface ?? '');
    setAliases(format.aliases.join(', '));
    setW(String(format.w));
    setH(String(format.h));
    setStatus(format.status);
    setNotes(format.notes ?? '');
  }, [format]);

  const sizeInvalid = !positiveInteger(w) || !positiveInteger(h);
  const nameMissing = name.trim() === '';

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (sizeInvalid || nameMissing) return;
    onSave({
      name: name.trim(),
      surface: surface.trim() || null,
      aliases: aliases
        .split(',')
        .map((alias) => alias.trim())
        .filter(Boolean),
      w: Number(w),
      h: Number(h),
      status,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={submit} className="flex max-w-2xl flex-col gap-md">
      <Field>
        <FieldLabel htmlFor="format-key">{t('admin.adFormats.fields.key')}</FieldLabel>
        <Input id="format-key" value={format.key} readOnly />
        <FieldDescription>{t('admin.adFormats.fields.keyHint')}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="format-name">{t('admin.adFormats.fields.name')}</FieldLabel>
        <Input id="format-name" value={name} onChange={(event) => setName(event.target.value)} />
        {nameMissing && <FieldError>{t('admin.adFormats.newFormatDialog.required')}</FieldError>}
      </Field>
      <div className="grid grid-cols-2 gap-md">
        <Field>
          <FieldLabel htmlFor="format-w">{t('admin.adFormats.fields.width')}</FieldLabel>
          <Input
            id="format-w"
            inputMode="numeric"
            value={w}
            onChange={(event) => setW(event.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="format-h">{t('admin.adFormats.fields.height')}</FieldLabel>
          <Input
            id="format-h"
            inputMode="numeric"
            value={h}
            onChange={(event) => setH(event.target.value)}
          />
        </Field>
      </div>
      {sizeInvalid && <FieldError>{t('admin.adFormats.newFormatDialog.sizeInvalid')}</FieldError>}
      <Field>
        <FieldLabel htmlFor="format-surface">{t('admin.adFormats.fields.surface')}</FieldLabel>
        <Input
          id="format-surface"
          value={surface}
          onChange={(event) => setSurface(event.target.value)}
        />
        <FieldDescription>{t('admin.adFormats.fields.surfaceHint')}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="format-aliases">{t('admin.adFormats.fields.aliases')}</FieldLabel>
        <Input
          id="format-aliases"
          value={aliases}
          onChange={(event) => setAliases(event.target.value)}
        />
        <FieldDescription>{t('admin.adFormats.fields.aliasesHint')}</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="format-status">{t('admin.adFormats.fields.status')}</FieldLabel>
        <Select value={status} onValueChange={(next) => setStatus(next as RecordStatus)}>
          <SelectTrigger id="format-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* eslint-disable-next-line i18next/no-literal-string -- status wire value */}
            <SelectItem value="active">{t('admin.adFormats.status.active')}</SelectItem>
            {/* eslint-disable-next-line i18next/no-literal-string -- status wire value */}
            <SelectItem value="deprecated">{t('admin.adFormats.status.deprecated')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field>
        <FieldLabel htmlFor="format-notes">{t('admin.adFormats.fields.notes')}</FieldLabel>
        <Textarea
          id="format-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </Field>
      <div>
        <Button type="submit" disabled={isSaving || sizeInvalid || nameMissing}>
          {isSaving ? t('admin.adFormats.saving') : t('admin.adFormats.save')}
        </Button>
      </div>
    </form>
  );
}
